import { yupResolver } from '@hookform/resolvers/yup';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { HotelModel } from 'api-hooks/hotel';
import { NavigationRoutes } from 'common/constants';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import { SeparatorVertical } from 'components/styles';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import { CardContainer } from 'mantine-components/container/card-container';
import {
  FullGridContainer,
  HalfGridContainer,
} from 'mantine-components/container/grid-container';
import Input from 'mantine-components/elements';
import Text from 'mantine-components/widget/text';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UniskaiFormHeaderRef } from 'templates/form/uniskai-form-header';
import UniskaiFormTemplate from 'templates/form/uniskai-form-template';

import { HotelFormSchema, HotelFormType } from './form-type';
import RoomSection from './room-section';
import useCruiseDefaultValues from '../hooks/use-hotel-default-values';

interface HotelFormProps {
  query?: UseQueryResult<ApiResult<HotelModel>, ApiError>;
  onSubmit: (
    input: HotelFormType,
    form: UseFormReturn<HotelFormType>,
  ) => Promise<HotelModel>;
  saveMutation?: UseMutationResult;
}

export default function HotelForm(props: HotelFormProps) {
  const { t } = useTranslation();
  const hotel = props.query?.data?.data;
  const defaultValues = useCruiseDefaultValues(hotel);
  const permission = useGetCrudPermission('hotels');
  const { deleteMutate, onDelete } = useDestroyAction('hotels');

  const titleRef = React.useRef<UniskaiFormHeaderRef>(null);
  React.useEffect(() => {
    let title = t(`pathname:${NavigationRoutes.HotelsCreate}`);
    if (hotel) {
      title = t(`pathname:${NavigationRoutes.HotelsView}`, {
        extra: hotel.name,
      });
    }
    titleRef.current?.setTitle(title);
  }, [t, hotel]);

  return (
    <UniskaiFormTemplate
      formProps={{
        defaultEditable: !hotel,
        defaultValues,
        resolver: yupResolver(HotelFormSchema(t)),
        onSubmit: props.onSubmit as any,
      }}
      customs={[
        {
          type: 'delete',
          loading: deleteMutate.isLoading,
          onClick: () => onDelete(hotel?.id),
          show: permission.delete,
        },
        {
          type: 'edit',
          show: permission.update,
        },
      ]}
      data={hotel}
      query={props.query}
      titleRef={titleRef}
      saveMutation={props.saveMutation}
    >
      <CardContainer>
        <Text variant="headingXSmall">{t('hotel:hotel_detail')}</Text>
        <SeparatorVertical $margin={8} />
        <HalfGridContainer>
          <Input
            type="text"
            name="area"
            label={t('common:area')}
            placeholder={t('common:area')}
          />
          <Input
            type="text"
            name="name"
            label={t('common:name')}
            placeholder={t('common:name')}
            required
          />
          <Input
            type="text"
            name="yearFounded"
            label={t('hotel:year_founded')}
            placeholder={t('hotel:year_founded')}
          />
          <Input
            type="text"
            name="cityCountry"
            label={[t('common:city'), t('common:country')].join(', ')}
            placeholder={[t('common:city'), t('common:country')].join(', ')}
            required
          />
          <Input
            type="text"
            name="email"
            label={t('common:email')}
            placeholder={t('common:email')}
          />
          <Input
            type="text"
            name="phone"
            label={t('hotel:tel_number')}
            placeholder={t('hotel:tel_number')}
          />
        </HalfGridContainer>
        <SeparatorVertical $margin={8} />
        <FullGridContainer>
          <Input
            type="text"
            name="address"
            label={t('common:address')}
            placeholder={t('common:address')}
            required
            fontCase="TitleCase"
          />
        </FullGridContainer>
        <SeparatorVertical $margin={8} />
        <HalfGridContainer>
          <Input
            type="time"
            name="checkIn"
            label={t('common:check_in')}
            placeholder={t('common:check_in')}
          />
          <Input
            type="time"
            name="checkOut"
            label={t('common:check_out')}
            placeholder={t('common:check_out')}
          />
        </HalfGridContainer>
        <SeparatorVertical $margin={8} />
        <FullGridContainer>
          <Input
            type="textarea"
            name="description"
            label={t('common:description')}
            placeholder={t('common:description')}
          />
        </FullGridContainer>
        <SeparatorVertical $margin={8} />
        <FullGridContainer>
          <Input
            key={+new Date()}
            type="editor"
            name="regulation"
            label={t('hotel:hotel_regulations')}
            placeholder={t('hotel:hotel_regulations')}
            controls={['default']}
            noMargin
          />
        </FullGridContainer>
        <SeparatorVertical $margin={8} />
        <FullGridContainer>
          <Input
            key={+new Date()}
            type="editor"
            name="mandatoryFee"
            label={t('hotel:mandatory_fee')}
            placeholder={t('hotel:mandatory_fee')}
            controls={['default']}
            noMargin
          />
        </FullGridContainer>
      </CardContainer>
      <SeparatorVertical $margin={24} />
      <RoomSection />
      <SeparatorVertical $margin={32} />
    </UniskaiFormTemplate>
  );
}
